import type { Action, Feature, RolePermissions, SensitiveSwitch } from "./permissions.js";

/**
 * Permission levers (D57; plan: docs/briefs/roles-granular.md).
 *
 * A lever is one assigned / not-assigned power. Levers sit in groups; a group's master switch
 * is a UI convenience over the effective lever set, never stored and never checked by the
 * server. This file is pure data and pure functions: no I/O, no other workspace packages.
 *
 * Every lever carries the `legacy` expression it replaces: the exact old grant check
 * (feature:action pairs and sensitive switches, ANDs included). `leversFromLegacy` uses it to
 * derive a role's lever set from its old grants, so the migration neither gains nor loses
 * access. Where two surfaces disagreed on `main`, the narrowest gate is used.
 */

/** The old grant check a lever replaces. Used only by the migration and the equivalence tests. */
export type LegacyExpr =
  | { kind: "feature"; feature: Feature; action: Action }
  | { kind: "switch"; switch: SensitiveSwitch }
  | { kind: "all"; of: readonly LegacyExpr[] }
  | { kind: "any"; of: readonly LegacyExpr[] }
  /**
   * Only the owner role passes today (the handler checks `role.isOwner`). Old grants can never satisfy
   * it, so the migration derives the lever off for every custom role; the owner holds every lever implicitly.
   */
  | { kind: "owner" };

const f = (feature: Feature, action: Action): LegacyExpr => ({ kind: "feature", feature, action });
const sw = (name: SensitiveSwitch): LegacyExpr => ({ kind: "switch", switch: name });
const all = (...of: LegacyExpr[]): LegacyExpr => ({ kind: "all", of });
/**
 * The legacy expression of a lever added after the move to levers: no old grant satisfies it, so the migration and
 * read-through never derive it and the legacy projection never needs it. It ships off for every role but the owner
 * (D47); a person turns it on in the Roles screen.
 */
const NEVER: LegacyExpr = { kind: "any", of: [] };
const ownerOnly: LegacyExpr = { kind: "owner" };

/**
 * Registry version. Bump when a lever is added; a post-release lever's `addedIn` is the new version.
 * v2 (D57 split): kiosks, bridges, expense categories, payees and recurring were split so each
 * lever matches exactly one old route gate. Nothing had been stored against v1, and the migration
 * derives these levers from legacy grants, so they keep `addedIn: 1` (they must not ship "off").
 * v3a (D57 split, slice 2b): `revenue.refund` stood for two gates on main: executing a refund
 * (payments:edit AND refund) and settling a credit note or a plan cancellation as a refund (the
 * refund switch alone). `revenue.refund_settlement` is the second; same `addedIn: 1` rule.
 * v3b (staff documents): staff.documents_view, staff.documents_edit, staff.bank_view_full and
 * staff.documents_download, `addedIn: 3`. Lever storage is on `main` now, so a role already stored at
 * v2 does not hold them and the Roles screen marks them New (D47); a role still on legacy grants
 * derives the first two from staff:view / staff:edit. The owner-only two derive off for every custom role.
 * v4: `billing.credit_note_over_cap` (founder ruling on #61, 2026-11-03, D59), owner approval for a
 * credit note above the role's discount cap. Owner-only legacy: no old grant derives it, so it ships
 * off for every custom role and only the owner holds it until the owner grants it.
 * v4 also: security review 2026-11-03, issues #63 and #64: five new levers that ship off (`addedIn: 4`, legacy NEVER).
 * Your own attendance and schedule are separate from everyone else's (`staff_attendance.correct_own`,
 * `staff.schedule_own`), and pay and money amounts in the audit log are separate from reading the log
 * (`roles.audit_payroll_amounts`, `roles.audit_commission_amounts`, `roles.audit_expense_amounts`). Acting on your own
 * pay (#62) is not a lever at all: nobody but the owner may.
 */
export const LEVER_REGISTRY_VERSION = 4;

export const GROUPS = [
  { id: "members", label: "Members" },
  { id: "leads", label: "Leads" },
  { id: "billing", label: "Billing" },
  { id: "revenue", label: "Revenue" },
  { id: "reports", label: "Reports" },
  { id: "pt", label: "Personal training" },
  { id: "classes", label: "Classes" },
  { id: "staff", label: "Staff" },
  { id: "staff_attendance", label: "Staff attendance" },
  { id: "payroll", label: "Payroll" },
  { id: "commission", label: "Commission" },
  { id: "expenses", label: "Expenses" },
  { id: "catalogue", label: "Catalogue" },
  { id: "inbox", label: "Inbox" },
  { id: "devices", label: "Devices" },
  { id: "settings", label: "Settings" },
  { id: "exports", label: "Exports" },
  { id: "roles", label: "Roles and audit" },
] as const;
export type GroupId = (typeof GROUPS)[number]["id"];

type Row = readonly [id: string, label: string, hint: string, legacy: LegacyExpr, requires?: readonly string[], addedIn?: number];

/** Money reports need `see_revenue` on top of `reports:view`, and every export needs `export_data`. */
const rv = f("reports", "view");
const re = f("reports", "export");
const money = sw("see_revenue");
const exp = sw("export_data");

const SETTINGS_SECTIONS = [
  ["business", "Business"],
  ["brands_branches", "Brands and branches"],
  ["invoicing", "Invoicing"],
  ["payment_accounts", "Payment accounts"],
  ["memberships", "Membership rules"],
  ["leads_setup", "Lead stages and intake keys"],
  ["attendance", "Attendance rules"],
  ["biometric", "Biometric rules"],
  ["commission", "Commission settings"],
  ["payroll", "Payroll settings"],
  ["messaging", "Messaging connections and templates"],
  ["imports", "Imports and onboarding"],
] as const;

const settingsRows = SETTINGS_SECTIONS.flatMap(([key, label]): Row[] => [
  [`settings.${key}_view`, `See ${label.toLowerCase()}`, `Open the ${label.toLowerCase()} settings.`, f("settings", "view")],
  [
    `settings.${key}_edit`,
    `Change ${label.toLowerCase()}`,
    `Save changes to the ${label.toLowerCase()} settings.`,
    f("settings", "edit"),
    // Compound: mapping imported roles (PUT /imports/:id/roles) also needs roles:view on main.
    key === "imports" ? [`settings.${key}_view`, "roles.roles_view"] : [`settings.${key}_view`],
  ],
]);

const ROWS = [
  // Members (15)
  ["members.view", "See members", "Member list and profiles.", f("members", "view")],
  ["members.add", "Add members", "Create a member record.", f("members", "add")],
  ["members.edit_profile", "Edit member profiles", "Change a member's details and date of birth.", f("members", "edit")],
  ["members.see_phone", "See member phone numbers", "Unmasked phone numbers on member records.", sw("see_phone")],
  ["members.freeze", "Freeze and unfreeze plans", "Includes unfreeze and bulk freeze.", f("members", "edit"), ["billing.create_bill"]],
  ["members.extend", "Extend plans", "Add days to a membership.", f("members", "edit")],
  ["members.transfer", "Transfer a plan", "Move a plan to another member.", f("members", "edit"), ["billing.create_bill"]],
  ["members.cancel_plan", "Cancel a plan", "End a membership early.", all(f("members", "edit"), f("billing", "add")), ["revenue.refund_settlement"]],
  ["members.override_start", "Override a plan start date", "Start a plan on a date other than the default.", f("members", "edit")],
  ["members.biometric_consent", "Record member biometric consent", "Record or withdraw a member's consent.", f("members", "edit")],
  ["members.checkins", "See check-in history", "Check-in log, per member and for the whole gym.", f("members", "view")],
  ["members.at_risk", "See at-risk members", "Members who have stopped coming.", f("members", "view")],
  ["members.opt_out", "Manage message opt-outs", "Opt a member out of or back into messages.", f("members", "edit")],
  ["members.message_log", "See the message log", "Messages sent to members and the opt-out list.", f("members", "view")],
  ["members.frontdesk_code", "Set a member's front-desk code", "Issue or reset a member's front-desk code.", f("members", "edit")],

  // Leads (7)
  ["leads.view", "See leads", "Lead board, list and profiles.", f("leads", "view")],
  ["leads.add", "Add leads", "Create a lead.", f("leads", "add")],
  ["leads.edit", "Edit leads", "Change a lead's details and tasks.", f("leads", "edit")],
  ["leads.move", "Move leads between stages", "Change a lead's stage.", f("leads", "edit")],
  ["leads.convert", "Convert leads to members", "Turn a lead into a member.", all(f("leads", "edit"), f("members", "add"))],
  ["leads.delete", "Delete leads", "Remove a lead.", f("leads", "delete")],
  ["leads.see_phone", "See lead phone numbers", "Unmasked phone numbers on leads.", sw("see_phone")],

  // Billing (11)
  ["billing.bills_view", "See bills and invoices", "Bills, invoices and credit notes.", f("billing", "view")],
  ["billing.create_bill", "Create bills", "Sell a plan or item.", f("billing", "add")],
  ["billing.renew", "Renew plans", "Renew a membership with a new bill.", f("billing", "add")],
  ["billing.upgrade", "Upgrade plans", "Move a member to a higher plan.", all(f("billing", "add"), f("members", "edit"))],
  ["billing.discount", "Give discounts", "Discount a bill, up to the role's cap.", f("billing", "add"), ["billing.create_bill"]],
  ["billing.credit_note", "Issue credit notes", "Credit part or all of a bill.", f("billing", "add")],
  [
    "billing.credit_note_over_cap",
    "Credit notes above the discount cap",
    "Credit a plan beyond the role's discount cap, or credit any line to the wallet beyond it. Owner approval.",
    ownerOnly,
    ["billing.credit_note"],
  ],
  ["billing.collect_payment", "Collect payments", "Record a payment against a bill.", f("payments", "add"), ["billing.create_bill"]],
  ["billing.payments_view", "See payments", "Payment list and payment intents.", f("payments", "view")],
  ["billing.payment_intent_manage", "Manage payment links", "Cancel or re-check a payment intent.", f("payments", "edit")],
  ["billing.dues", "See dues", "Who owes what, on Home and the dues page.", all(f("billing", "view"), money)],

  // Revenue (8)
  ["revenue.home_collections", "See collections on Home", "The collected-today figure.", all(f("payments", "view"), money)],
  ["revenue.bill_amounts", "See bill amounts", "Amounts on bills, payments, invoices and refunds.", all(f("billing", "view"), money)],
  ["revenue.member_balance", "See member balances", "A member's balance and wallet.", money],
  ["revenue.refunds_view", "See refunds", "The refunds list.", f("payments", "view")],
  ["revenue.refund", "Give refunds", "Execute a refund.", all(f("payments", "edit"), sw("refund"))],
  ["revenue.refund_settlement", "Settle as a refund", "Pay a credit note or a cancelled plan back as a refund.", sw("refund")],
  ["revenue.void_bill", "Void bills", "Void a bill after the fact.", all(f("billing", "edit"), sw("edit_past_payment"))],
  ["revenue.resolve_payment_review", "Resolve payments under review", "Settle a payment held for review.", all(f("payments", "edit"), sw("edit_past_payment"))],

  // Reports (8)
  ["reports.collections", "Collections report", "Money collected over a period.", all(rv, money)],
  ["reports.dues", "Dues report", "Outstanding dues.", all(rv, money)],
  ["reports.renewals_due", "Renewals-due report", "Plans about to expire.", rv],
  ["reports.attendance", "Attendance report", "Member visits over a period.", rv],
  ["reports.pt_delivery", "PT delivery report", "Personal-training sessions delivered.", rv],
  ["reports.payroll", "Payroll report", "Payroll totals.", all(rv, money)],
  ["reports.lead_funnel", "Lead funnel report", "Leads by stage and source.", rv],
  ["reports.expenses", "Expenses report", "Spend by category.", all(rv, money)],

  // PT (12)
  ["pt.view", "See PT sessions", "PT calendar and sessions.", f("pt_calendar", "view")],
  ["pt.book", "Book PT sessions", "Book a personal-training session.", f("pt_calendar", "add"), ["staff.see_trainer_names"]],
  ["pt.reschedule", "Reschedule PT sessions", "Move a session.", f("pt_calendar", "edit")],
  ["pt.override", "Override PT scheduling rules", "Reschedule outside the normal rules.", all(f("pt_calendar", "edit"), f("pt_calendar", "delete")), ["pt.reschedule"]],
  ["pt.cancel", "Cancel PT sessions", "Cancel a session.", f("pt_calendar", "edit")],
  ["pt.complete", "Complete PT sessions", "Mark a session done or a no-show.", f("pt_calendar", "edit")],
  ["pt.waive", "Waive PT sessions", "Waive a session without delivery.", f("pt_calendar", "delete")],
  ["pt.change_trainer", "Change a session's trainer", "Hand a session to another trainer.", f("pt_calendar", "edit"), ["staff.see_trainer_names"]],
  ["pt.availability", "Set trainer availability", "Edit a trainer's open hours.", f("pt_calendar", "edit")],
  ["pt.pack_balance", "See PT pack balances", "Sessions left on a pack.", f("pt_calendar", "view")],
  ["pt.schedule_pattern", "Book and change PT patterns", "Book a pack on a fixed weekly slot, or move its future sessions.", f("pt_calendar", "edit")],
  ["pt.auto_mark_settings", "Change PT auto-marking", "Turn marking from check-ins and end of day on or off, and set the booking style.", f("settings", "edit")],

  // Classes (11)
  ["classes.view", "See classes", "Class schedule and bookings.", f("classes", "view")],
  ["classes.template_add", "Add class templates", "Create a recurring class.", f("classes", "add"), ["staff.see_trainer_names"]],
  ["classes.template_edit", "Edit class templates", "Change a recurring class and its exceptions.", f("classes", "edit"), ["staff.see_trainer_names"]],
  ["classes.template_delete", "Delete class templates", "Remove a recurring class.", f("classes", "delete")],
  ["classes.book", "Book members into classes", "Reserve a class place.", f("classes", "add")],
  ["classes.cancel_occurrence", "Cancel a class occurrence", "Cancel one class.", f("classes", "edit")],
  ["classes.complete_occurrence", "Complete a class occurrence", "Mark one class as held.", f("classes", "edit")],
  ["classes.cancel_booking", "Cancel class bookings", "Cancel a member's booking.", f("classes", "edit")],
  ["classes.attendance", "Mark class attendance", "Record who attended.", f("classes", "edit")],
  ["classes.waive", "Waive class bookings", "Waive a late-cancel or no-show charge.", f("classes", "delete")],
  ["classes.refunds_due", "See class refunds due", "Bookings owed a refund.", f("classes", "view")],

  // Staff (16)
  ["staff.view", "See staff", "Staff list and profiles.", f("staff", "view")],
  ["staff.add", "Add staff", "Invite a staff member.", f("staff", "add")],
  ["staff.edit_profile", "Edit staff profiles", "Change a staff member's details.", f("staff", "edit")],
  ["staff.schedule", "Edit staff schedules", "Set weekly working hours.", f("staff", "edit")],
  ["staff.schedule_own", "Edit own schedule", "Set your own weekly working hours, which change your absence deductions.", NEVER, ["staff.schedule"], 4],
  ["staff.deactivate", "Deactivate staff", "Remove a staff member's access.", f("staff", "delete")],
  ["staff.see_phone", "See staff phone numbers", "Unmasked phone numbers on staff records.", sw("see_phone")],
  ["staff.biometric_consent", "Record staff biometric consent", "Record or withdraw a staff member's consent.", f("staff", "edit")],
  ["staff.tasks_assign", "Assign tasks to others", "Give a task to another staff member.", f("staff", "edit")],
  ["staff.tasks_view_others", "See others' tasks", "Team view of tasks.", f("staff", "view")],
  ["staff.see_names", "See colleagues' names", "Names on lead owners, task assignees and the audit log.", f("staff", "view")],
  ["staff.see_trainer_names", "See trainer names", "Names on PT, class schedules, templates and pickers.", f("staff", "view")],
  ["staff.documents_view", "See staff ID and bank details", "PAN, Aadhaar last 4, date of birth, address, emergency contact, masked bank account and the document list.", f("staff", "view")],
  ["staff.documents_edit", "Edit staff ID and bank details", "Change those details, upload and delete documents.", f("staff", "edit"), ["staff.documents_view"]],
  ["staff.bank_view_full", "See full bank account numbers", "Reveal a staff member's full account number. Every reveal is audited.", ownerOnly, ["staff.documents_view"]],
  ["staff.documents_download", "Download staff documents", "Open PAN cards, ID proofs, cheques and offer letters. Every download is audited.", ownerOnly, ["staff.documents_view"]],

  // Staff attendance (4)
  ["staff_attendance.view", "See staff attendance", "Staff punches and days.", f("staff", "view")],
  ["staff_attendance.correct", "Correct staff attendance", "Add punches and fix days.", f("staff", "edit")],
  ["staff_attendance.correct_own", "Correct own attendance", "Add punches to and fix days on your own record, which changes your pay.", NEVER, ["staff_attendance.correct"], 4],
  ["staff_attendance.holidays", "Manage the holiday calendar", "Add and remove holidays.", f("settings", "edit")],

  // Payroll (7)
  ["payroll.view", "See payroll", "Payroll months, profiles and payslips.", f("payroll", "view")],
  ["payroll.profiles_edit", "Edit payroll profiles", "Pay terms for each person.", f("payroll", "edit")],
  ["payroll.advances", "Record advances and adjustments", "Advances and one-off adjustments.", f("payroll", "add")],
  ["payroll.draft", "Draft a payroll month", "Build the month's payslips.", f("payroll", "add")],
  ["payroll.approve", "Approve payroll", "Approve a drafted month.", f("payroll", "edit")],
  ["payroll.lock", "Lock payroll", "Lock an approved month.", f("payroll", "edit")],
  ["payroll.mark_paid", "Mark payslips paid", "Record that a payslip was paid.", f("payroll", "edit")],

  // Commission (6)
  ["commission.rules_view", "See commission rules", "Rule list and versions.", f("commission", "view")],
  ["commission.rules_edit", "Edit commission rules", "Create, version and retire rules.", f("commission", "edit")],
  ["commission.preview", "Preview commission", "Dry-run commission before posting.", f("commission", "view")],
  ["commission.post", "Post commission", "Post commission to the ledger.", f("commission", "edit")],
  ["commission.ledger_view", "See the commission ledger", "Ledger entries.", f("commission", "view")],
  ["commission.release", "Release held commission", "Release commission that was held.", f("commission", "edit")],

  // Expenses (15)
  ["expenses.view", "See expenses", "Expense list and details.", f("expenses", "view")],
  ["expenses.add", "Add expenses", "Record an expense.", f("expenses", "add")],
  ["expenses.edit", "Edit expenses", "Change an expense.", f("expenses", "edit")],
  ["expenses.delete", "Delete expenses", "Remove an expense.", f("expenses", "delete")],
  ["expenses.categories_add_edit", "Add and rename expense categories", "Create a category or change its name.", f("expenses", "edit")],
  ["expenses.categories_delete", "Delete expense categories", "Remove a category.", f("expenses", "delete")],
  ["expenses.payees_add_edit", "Add and change payees", "Create a payee or change their details.", f("expenses", "edit")],
  ["expenses.payees_delete", "Delete payees", "Remove a payee.", f("expenses", "delete")],
  ["expenses.recurring_add", "Add recurring expenses", "Create a repeating expense.", f("expenses", "add")],
  ["expenses.recurring_edit", "Change recurring expenses", "Edit a repeating expense.", f("expenses", "edit")],
  ["expenses.recurring_delete", "Delete recurring expenses", "Remove a repeating expense.", f("expenses", "delete")],
  ["expenses.recurring_post", "Post recurring expenses", "Post a repeating expense now.", f("expenses", "add")],
  ["expenses.attachments", "Manage expense attachments", "Add and remove receipts.", f("expenses", "edit")],
  ["expenses.payee_phone", "See payee phone numbers", "Unmasked phone numbers on payees.", sw("see_phone")],
  ["expenses.import", "Import expenses from a bank statement", "Pick transactions from a statement file and add them as expenses.", f("expenses", "add"), ["expenses.add"]],

  // Catalogue (8)
  ["catalogue.plans_view", "See plans and items", "The catalogue.", f("plans", "view")],
  ["catalogue.plans_add", "Add plans and items", "Create a catalogue item.", f("plans", "add")],
  ["catalogue.plans_edit", "Edit plans and items", "Change a catalogue item.", f("plans", "edit")],
  ["catalogue.plans_delete", "Delete plans and items", "Remove a catalogue item.", f("plans", "delete")],
  ["catalogue.coupons_view", "See coupons", "Coupon list.", f("plans", "view")],
  ["catalogue.coupons_add", "Add coupons", "Create a coupon.", f("plans", "add")],
  ["catalogue.coupons_edit", "Edit coupons", "Change a coupon.", f("plans", "edit")],
  ["catalogue.coupons_delete", "Delete coupons", "Remove a coupon.", f("plans", "delete")],

  // Inbox (3)
  ["inbox.view", "See the inbox", "Conversations and messages.", f("inbox", "view")],
  ["inbox.reply", "Reply in the inbox", "Send text or template messages.", f("inbox", "edit")],
  ["inbox.manage", "Assign and archive conversations", "Assignment, archive and read state.", f("inbox", "edit")],

  // Devices (10)
  ["devices.view", "See devices", "Kiosks, bridges and terminals.", f("devices", "view")],
  ["devices.kiosks_add", "Add kiosks", "Create a check-in kiosk.", f("devices", "add")],
  ["devices.kiosks_edit", "Change kiosks", "Edit a check-in kiosk.", f("devices", "edit")],
  ["devices.kiosk_revoke", "Revoke kiosks", "Remove a kiosk's access.", f("devices", "delete")],
  ["devices.bridges_add", "Add bridges", "Add a device bridge.", f("devices", "add")],
  ["devices.bridges_remove", "Remove bridges", "Remove a device bridge.", f("devices", "delete")],
  ["devices.terminals_map", "Map terminals", "Rename terminals and map their users.", f("devices", "edit")],
  ["devices.biometric_enrol", "Enrol and copy biometrics", "Enrol a member or staff member, or copy to another terminal.", f("devices", "edit")],
  ["devices.biometric_wipe", "Wipe biometrics", "Remove a person's biometric from terminals.", f("devices", "delete")],
  ["devices.checkin_keys", "Rotate check-in keys", "Rotate the signed-code keys.", f("devices", "edit")],

  // Settings (24)
  ...settingsRows,

  // Exports (15)
  ["exports.leads", "Export leads", "Download leads as CSV.", all(f("leads", "export"), exp)],
  ["exports.bills", "Export bills", "Download bills as CSV.", all(f("billing", "export"), exp)],
  ["exports.invoices", "Export invoices", "Download invoices as CSV.", all(f("billing", "export"), exp)],
  ["exports.staff_attendance", "Export staff attendance", "Download staff attendance as CSV.", all(f("staff", "export"), exp)],
  ["exports.commission_ledger", "Export the commission ledger", "Download the ledger as CSV.", all(f("commission", "export"), exp)],
  ["exports.payroll", "Export payroll", "Download payroll as CSV.", all(f("payroll", "export"), exp)],
  ["exports.expenses", "Export expenses", "Download expenses as CSV.", all(f("expenses", "export"), exp)],
  ["exports.report_collections", "Export the collections report", "Download as CSV.", all(re, exp, money)],
  ["exports.report_dues", "Export the dues report", "Download as CSV.", all(re, exp, money)],
  ["exports.report_renewals_due", "Export the renewals-due report", "Download as CSV.", all(re, exp)],
  ["exports.report_attendance", "Export the attendance report", "Download as CSV.", all(re, exp)],
  ["exports.report_pt_delivery", "Export the PT delivery report", "Download as CSV.", all(re, exp)],
  ["exports.report_payroll", "Export the payroll report", "Download as CSV.", all(re, exp, money)],
  ["exports.report_lead_funnel", "Export the lead funnel report", "Download as CSV.", all(re, exp)],
  ["exports.report_expenses", "Export the expenses report", "Download as CSV.", all(re, exp, money)],

  // Roles and audit (9)
  ["roles.roles_view", "See roles", "Role list and details.", f("roles", "view")],
  ["roles.roles_add", "Create roles", "Add a custom role.", f("roles", "add")],
  ["roles.roles_edit", "Edit roles", "Change a role's levers and scope.", f("roles", "edit")],
  ["roles.roles_delete", "Delete roles", "Remove a custom role.", f("roles", "delete")],
  ["roles.roles_assign", "Assign roles to staff", "Change which role a person holds.", all(f("staff", "edit"), f("roles", "edit"))],
  ["roles.audit_view", "See the audit log", "Who did what, and when.", f("audit", "view")],
  ["roles.audit_payroll_amounts", "See pay in the audit log", "Salaries, TDS, advances, adjustments and payslip amounts on payroll entries.", NEVER, ["roles.audit_view"], 4],
  ["roles.audit_commission_amounts", "See commission in the audit log", "Rule rates and posted amounts on commission entries.", NEVER, ["roles.audit_view"], 4],
  ["roles.audit_expense_amounts", "See expenses in the audit log", "Amounts and details on expense entries.", NEVER, ["roles.audit_view"], 4],
] as const satisfies readonly Row[];

export type LeverId = (typeof ROWS)[number][0];

export interface LeverDef {
  id: LeverId;
  group: GroupId;
  label: string;
  /** One line shown under the switch. */
  hint: string;
  /** Registry version that introduced it. */
  addedIn: number;
  /** Shown in the UI as "Needs: ..."; never auto-toggled. */
  requires?: LeverId[];
  /** Every check writes an audit row: true where the old check was a sensitive switch. */
  audited?: boolean;
  /** The old grant expression this lever replaces (used only by the migration). */
  legacy: LegacyExpr;
}

/** A sensitive switch or an owner-only check: both write an audit row on every use. */
function usesSwitch(e: LegacyExpr): boolean {
  if (e.kind === "switch" || e.kind === "owner") return true;
  if (e.kind === "feature") return false;
  return e.of.some(usesSwitch);
}

/** Levers added after the first stored registry: their `addedIn`. */
const ADDED_IN: Readonly<Record<string, number>> = {
  // bank-statement import. Legacy = expenses:add, so every role that can add an expense can import.
  "expenses.import": 3,
  "staff.documents_view": 3,
  "staff.documents_edit": 3,
  "staff.bank_view_full": 3,
  "staff.documents_download": 3,
  // pt-schedules: each carries the legacy gate of the route it guards, so nobody gains or loses access.
  "pt.schedule_pattern": 3,
  "pt.auto_mark_settings": 3,
  // Founder ruling on #61 (D59): owner approval above the discount cap. Owner-only, ships off.
  "billing.credit_note_over_cap": 4,
};

export const LEVERS: readonly LeverDef[] = (ROWS as readonly Row[]).map(([id, label, hint, legacy, requires, rowAddedIn]) => {
  const def: LeverDef = {
    id: id as LeverId,
    group: id.slice(0, id.indexOf(".")) as GroupId,
    label,
    hint,
    addedIn: rowAddedIn ?? ADDED_IN[id] ?? 1,
    legacy,
  };
  if (requires) def.requires = [...requires] as LeverId[];
  if (usesSwitch(legacy)) def.audited = true;
  return def;
});

export const LEVER_IDS: readonly LeverId[] = LEVERS.map((l) => l.id);

const LEVER_BY_ID: ReadonlyMap<string, LeverDef> = new Map(LEVERS.map((l) => [l.id, l]));

export function isLeverId(value: unknown): value is LeverId {
  return typeof value === "string" && LEVER_BY_ID.has(value);
}

export function getLever(id: LeverId): LeverDef {
  return LEVER_BY_ID.get(id) as LeverDef;
}

export function isGroupId(value: unknown): value is GroupId {
  return typeof value === "string" && GROUPS.some((g) => g.id === value);
}

export function leversInGroup(group: GroupId): LeverId[] {
  return LEVERS.filter((l) => l.group === group).map((l) => l.id);
}

/** The subset of a role's old grants that the legacy expressions read. */
export type LegacyGrants = Pick<RolePermissions, "features" | "sensitive">;

export function satisfiesLegacy(grants: LegacyGrants, expr: LegacyExpr): boolean {
  switch (expr.kind) {
    case "feature":
      return grants.features[expr.feature]?.includes(expr.action) === true;
    case "switch":
      return grants.sensitive.includes(expr.switch);
    case "all":
      return expr.of.every((e) => satisfiesLegacy(grants, e));
    case "any":
      return expr.of.some((e) => satisfiesLegacy(grants, e));
    case "owner":
      return false;
  }
}

/** Every lever whose legacy expression the old grants satisfy, sorted by id. An empty grant gives `[]`. */
export function leversFromLegacy(grants: LegacyGrants): LeverId[] {
  return LEVERS.filter((l) => satisfiesLegacy(grants, l.legacy))
    .map((l) => l.id)
    .sort();
}

export type GroupState = "on" | "off" | "partial";

/** A group is on when every lever in it is held, off when none is, partial otherwise. */
export function groupState(levers: Iterable<string>, group: GroupId): GroupState {
  const held = new Set(levers);
  const ids = leversInGroup(group);
  const n = ids.filter((id) => held.has(id)).length;
  if (n === 0) return "off";
  return n === ids.length ? "on" : "partial";
}

export interface LeverDiff {
  added: LeverId[];
  removed: LeverId[];
}

/** What changes going from set `a` to set `b`; both lists sorted. */
export function diffLevers(a: Iterable<LeverId>, b: Iterable<LeverId>): LeverDiff {
  const from = new Set(a);
  const to = new Set(b);
  return {
    added: [...to].filter((id) => !from.has(id)).sort(),
    removed: [...from].filter((id) => !to.has(id)).sort(),
  };
}
